// @ts-check
// Regras puras das abas no rascunho do painel (doc 16, seção 8.4; contrato 07, seção 8.1). Cada aba tem a sua grade;
// um widget pertence a uma aba pelo `tabId`, que no rascunho é a chave da aba (o id, para as que já existem).
import { getBottomRow } from './dashboard-grid.js';

/**
 * @typedef {{ key: string, id?: string, title: string, icon?: string | null }} DraftTab
 * @typedef {import('./dashboard-grid.js').GridItem & { tabId?: string | null, title?: string }} TabbedItem
 */

export const MAXIMUM_DASHBOARD_TABS = 20;

/**
 * Widgets de uma aba. Sem aba (painel antigo em memória), vale a primeira.
 * @template {TabbedItem} TItem
 * @param {TItem[]} widgets @param {string | null} tabKey @param {string | null} firstTabKey
 * @returns {TItem[]}
 */
export function widgetsInTab(widgets, tabKey, firstTabKey) {
  return widgets.filter((widget) => (widget.tabId ?? firstTabKey) === tabKey);
}

/**
 * Aba que deve ficar aberta: a pedida se existir, senão a primeira.
 * @param {DraftTab[]} tabs @param {string | null | undefined} requestedKey
 */
export function resolveActiveTabKey(tabs, requestedKey) {
  if (requestedKey && tabs.some((tab) => tab.key === requestedKey)) return requestedKey;
  return tabs[0]?.key ?? null;
}

/**
 * Leva o widget para outra aba, na coluna 0 abaixo do último widget dela (como no Twenty).
 * @template {TabbedItem} TItem
 * @param {TItem[]} widgets @param {string} widgetKey @param {string} targetTabKey @param {string | null} firstTabKey
 * @returns {TItem[]}
 */
export function moveWidgetToTab(widgets, widgetKey, targetTabKey, firstTabKey) {
  const widget = widgets.find((candidate) => candidate.key === widgetKey);
  if (!widget || (widget.tabId ?? firstTabKey) === targetTabKey) return widgets;
  const gridRow = getBottomRow(widgetsInTab(widgets, targetTabKey, firstTabKey));
  return widgets.map((candidate) => (candidate.key === widgetKey ? { ...candidate, tabId: targetTabKey, gridRow, gridColumn: 0 } : candidate));
}

/**
 * Nova aba no fim, com o título informado.
 * @param {DraftTab[]} tabs @param {{ key: string, title: string }} tab
 * @returns {DraftTab[]}
 */
export function addTab(tabs, { key, title }) {
  if (tabs.length >= MAXIMUM_DASHBOARD_TABS) return tabs;
  return [...tabs, { key, title, icon: null }];
}

/**
 * Troca a aba de lugar com a vizinha (-1 esquerda, +1 direita).
 * @param {DraftTab[]} tabs @param {string} tabKey @param {-1 | 1} direction
 * @returns {DraftTab[]}
 */
export function moveTab(tabs, tabKey, direction) {
  const index = tabs.findIndex((tab) => tab.key === tabKey);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= tabs.length) return tabs;
  const next = [...tabs];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Exclui a aba e os widgets dela. A última aba não sai. Devolve também a aba que passa a ficar aberta (a vizinha
 * anterior ou, sem ela, a seguinte).
 * @template {TabbedItem} TItem
 * @param {DraftTab[]} tabs @param {TItem[]} widgets @param {string} tabKey
 * @returns {{ tabs: DraftTab[], widgets: TItem[], activeTabKey: string | null }}
 */
export function removeTab(tabs, widgets, tabKey) {
  const index = tabs.findIndex((tab) => tab.key === tabKey);
  if (index < 0 || tabs.length <= 1) return { tabs, widgets, activeTabKey: tabs[Math.max(0, index)]?.key ?? null };
  const firstTabKey = tabs[0].key;
  const remaining = tabs.filter((tab) => tab.key !== tabKey);
  return {
    tabs: remaining,
    widgets: widgets.filter((widget) => (widget.tabId ?? firstTabKey) !== tabKey),
    activeTabKey: (remaining[index - 1] ?? remaining[index] ?? remaining[0]).key,
  };
}

/**
 * Copia a aba (logo depois da original) e os widgets dela, com chaves novas e as mesmas posições.
 * @template {TabbedItem} TItem
 * @param {DraftTab[]} tabs @param {TItem[]} widgets @param {string} tabKey
 * @param {{ newTabKey: string, title: string, newWidgetKey: () => string }} options
 * @returns {{ tabs: DraftTab[], widgets: TItem[] }}
 */
export function duplicateTab(tabs, widgets, tabKey, { newTabKey, title, newWidgetKey }) {
  const index = tabs.findIndex((tab) => tab.key === tabKey);
  if (index < 0 || tabs.length >= MAXIMUM_DASHBOARD_TABS) return { tabs, widgets };
  const source = tabs[index];
  const copy = { key: newTabKey, title, icon: source.icon ?? null };
  const copies = widgetsInTab(widgets, tabKey, tabs[0].key).map((widget) => {
    const { id: _id, ...rest } = /** @type {TItem & { id?: string }} */ (structuredClone(widget));
    return /** @type {TItem} */ ({ ...rest, key: newWidgetKey(), tabId: newTabKey });
  });
  return { tabs: [...tabs.slice(0, index + 1), copy, ...tabs.slice(index + 1)], widgets: [...widgets, ...copies] };
}

/**
 * Abas como o PUT espera: as existentes pelo id, as novas pela chave que os widgets usam.
 * @param {DraftTab[]} tabs
 */
export function toSavedTabs(tabs) {
  return tabs.map((tab) => (tab.id ? { id: tab.id, title: tab.title.trim(), icon: tab.icon ?? null } : { key: tab.key, title: tab.title.trim(), icon: tab.icon ?? null }));
}

/**
 * Próximo título livre "Aba N".
 * @param {DraftTab[]} tabs @param {(number: number) => string} format
 */
export function nextTabTitle(tabs, format) {
  const titles = new Set(tabs.map((tab) => tab.title));
  let number = tabs.length + 1;
  while (titles.has(format(number))) number += 1;
  return format(number);
}
